import React, { useEffect } from 'react';
import {
  Alerts,
  ButtonAction,
  Header,
  PageContent,
  Table,
  Spinner,
  CisContainer,
} from 'components';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  loadMaClaimedCertificates,
  setMaClaimedCertificatesFiltersCreator,
  setMaClaimedCertificatesPageCreator,
  setMaClaimedCertificatesPerPageCreator,
  setMaClaimedCertificatesSortCreator,
} from '../../../store/actionsCreators';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import { isLoading, formatDate, parseParams } from 'helpers';
import { useState } from 'react';
import { exportCertificatesToZip } from '../../../api/services/ma';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.ma.certificates',
    url: '/ma/certificates',
  },
];

const Certificates = ({
  certifications,
  loadMaClaimedCertificates,
  loading,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
}) => {
  const { t } = useTranslation();
  const { filters, items, page, pages, perPage, sort, totalItems } = certifications;
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setPage(1);
    loadMaClaimedCertificates();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadMaClaimedCertificates();
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      // parseParams turns the picked Date filters into plain day strings; passing the
      // Date objects straight to qs would serialise them as UTC and shift the day.
      const filteredFilters = parseParams(
        Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
      );
      await exportCertificatesToZip(filteredFilters);
    } catch (error) {
      console.error('Error exporting certificates:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const renderHeaderActions = () => {
    return ({ Button: ButtonRender }) => (
      <ButtonRender secondary onClick={handleExport} disabled={isExporting}>
        {isExporting ? t('global.exporting') : t('page.ma.certificates.exportDisplayed')}
      </ButtonRender>
    );
  };

  return (
    <>
      <Header
        breadcrumbs={breadcrumbs}
        title={t('page.ma.certificates.title')}
        renderActions={renderHeaderActions()}
      />
      <PageContent>
        <CisContainer>
          <Alerts type="certificates" />
          <CisTable>
            <CisTable.Head>
              <CisTable.Row>
                <CisTable.StickyHeading>
                  <TableHeaderCell
                    filter
                    filterType="input"
                    filterValue={filters.ownerName || ''}
                    onFilter={(val) => handleColumnFilterChange('ownerName', val)}
                    filterPlaceholder={t('page.ma.certificates.ownerNamePlaceholder')}
                    label={t('certificates.ownerName')}
                    sort
                    sortDirection={getSortDirection('ownerName')}
                    onSort={(dir) => handleColumnSort('ownerName', dir)}
                  />
                </CisTable.StickyHeading>
                <CisTable.StickyHeading>
                  <TableHeaderCell
                    filter
                    filterType="input"
                    filterValue={filters.levelDomain || ''}
                    onFilter={(val) => handleColumnFilterChange('levelDomain', val)}
                    filterPlaceholder={t('page.ma.certificates.namePlaceholder')}
                    label={t('certificates.certificate')}
                  />
                </CisTable.StickyHeading>
                <CisTable.StickyHeading>
                  <TableHeaderCell
                    filter
                    filterType="input"
                    filterValue={filters.certificateId || ''}
                    onFilter={(val) => handleColumnFilterChange('certificateId', val)}
                    filterPlaceholder={t('page.ma.certificates.certificateIdPlaceholder')}
                    label={t('certificates.certificateNumber')}
                    sort
                    sortDirection={getSortDirection('certificateId')}
                    onSort={(dir) => handleColumnSort('certificateId', dir)}
                  />
                </CisTable.StickyHeading>
                <CisTable.StickyHeading>
                  <TableHeaderCell
                    filter
                    filterType="datepicker-range"
                    filterValueFrom={filters.issueDateFrom || ''}
                    filterValueTo={filters.issueDateTo || ''}
                    onFilterFrom={(val) => handleColumnFilterChange('issueDateFrom', val)}
                    onFilterTo={(val) => handleColumnFilterChange('issueDateTo', val)}
                    filterPlaceholder={t('page.ma.certificates.awardedDatePlaceholder')}
                    label={t('certificates.awardedDate')}
                  />
                </CisTable.StickyHeading>
                <CisTable.StickyHeading>
                  <TableHeaderCell
                    filter
                    filterType="datepicker-range"
                    filterValueFrom={filters.validUntilFrom || ''}
                    filterValueTo={filters.validUntilTo || ''}
                    onFilterFrom={(val) => handleColumnFilterChange('validUntilFrom', val)}
                    onFilterTo={(val) => handleColumnFilterChange('validUntilTo', val)}
                    filterPlaceholder={t('page.ma.certificates.validityDatePlaceholder')}
                    label={t('certificates.validityDate')}
                  />
                </CisTable.StickyHeading>
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('certificates.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              </CisTable.Row>
            </CisTable.Head>
            <CisTable.Body>
              {loading ? (
                <CisTable.Row>
                  <CisTable.Cell colSpan={6}>
                    <Spinner />
                  </CisTable.Cell>
                </CisTable.Row>
              ) : null}
              {!loading && !items.length ? (
                <CisTable.Row>
                  <CisTable.Cell colSpan={6}>{t('global.noResultsFound')}</CisTable.Cell>
                </CisTable.Row>
              ) : null}
              {!loading && items.length
                ? items.map((item) => (
                    <CisTable.Row key={item.id}>
                      <CisTable.Cell>
                        <Table.Label>{t('certificates.ownerName')}</Table.Label>
                        {[
                          item.certificateHolder?.firstName,
                          item.certificateHolder?.middleName,
                          item.certificateHolder?.lastName,
                        ].join(' ')}
                      </CisTable.Cell>
                      <CisTable.Cell>
                        <Table.Label>{t('certificates.certificate')}</Table.Label>
                        {item.levelDomain?.designation}
                      </CisTable.Cell>
                      <CisTable.Cell>
                        <Table.Label>{t('certificates.certificateNumber')}</Table.Label>
                        {item.certId}
                      </CisTable.Cell>
                      <CisTable.Cell>
                        <Table.Label>{t('certificates.awardedDate')}</Table.Label>
                        {formatDate(item.issueDate)}
                      </CisTable.Cell>
                      <CisTable.Cell>
                        <Table.Label>{t('certificates.validityDate')}</Table.Label>
                        {formatDate(item.validUntil)}
                      </CisTable.Cell>
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('certificates.actions')}:</Table.Label>
                        {item.certificateDocument?.fileUrl ? (
                          <ButtonAction
                            as={'a'}
                            href={item.certificateDocument.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t('global.downloadCertificate')}
                            icon="download"
                          />
                        ) : null}
                      </CisTable.StickyActionCell>
                    </CisTable.Row>
                  ))
                : null}
            </CisTable.Body>
          </CisTable>
        </CisContainer>
        <CisPagination
          styled
          currentPage={page}
          totalPages={pages}
          totalItems={totalItems}
          perPage={perPage}
          onPageChange={(payload) => {
            setPage(payload);
            loadMaClaimedCertificates();
          }}
          onPerPageChange={({ value }) => setPerPage(value)}
        />
      </PageContent>
    </>
  );
};

Certificates.defaultProps = {
  certifications: [],
};

const mapStateToProps = ({ ma, loading }) => ({
  certifications: ma.claimedCertificates,
  loading: isLoading(loading, 'LOAD_MA_CLAIMED_CERTIFICATES'),
});

const mapDispatchToProps = (dispatch) => ({
  loadMaClaimedCertificates: () => dispatch(loadMaClaimedCertificates()),
  setPage: (payload) => dispatch(setMaClaimedCertificatesPageCreator(payload)),
  setPerPage: (payload) => dispatch(setMaClaimedCertificatesPerPageCreator(payload)),
  setSort: (payload) => dispatch(setMaClaimedCertificatesSortCreator(payload)),
  setFilters: (payload) => dispatch(setMaClaimedCertificatesFiltersCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Certificates);
